import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import {
    Box, Button, CircularProgress, Grid, ListItemIcon, Menu, MenuItem, Typography, useTheme,
} from "@mui/material";
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBook';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremium';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNone';
import LogoutIcon from '@mui/icons-material/Logout';
import ResetPasswordImage from '../../images/icons/reset-password.png';
import LanguageImage from '../../images/icons/language.png';
import SyImage from '../../images/flags/sy.png';
import AuImage from '../../images/flags/au.png';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import UpdateProfile from "../../popup/UpdateProfile";
import { usePopups } from "../../hooks/UsePopups";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import UpdatePassword from "../../popup/UpdatePassword";
import { useWaits } from "../../hooks/UseWait";
import Fetch from "../../services/Fetch";
import { useConstants } from "../../hooks/UseConstants";
import QRCode from "react-qr-code";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageHero from "../../components/motion/PageHero";
import LogoImage from "../../images/logo/logo.png";
import { APP_NAME } from "../../constants/brand";
import ScrollReveal from "../../components/motion/ScrollReveal";

function Profile() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { setPopup } = usePopups();

    useEffect(() => {
        if (location.state?.openPassword) {
            setPopup('update-password', 'flex');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate, setPopup]);
    const { host, language } = useConstants();
    const { sendWait, setSendWait } = useWaits();
    const { wait, user, refreshUser } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [lang, setLang] = useState(language || 'ar');
    const isAr = (language || 'ar') === 'ar';

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const changeLanguage = async (nextLang) => {
        if (!user) return;
        setSendWait(true);
        const formData = new FormData();
        formData.append('language', nextLang);

        const result = await Fetch(host + '/account/update-profile', 'POST', formData);

        if (result.status === 200) {
            localStorage.setItem('language', nextLang);
            handleClose();
            setSnackBar('success', 'تم التحديث بنجاح');
            await refreshUser();
            window.location.reload();
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || 'تعذر التحديث');
        }

        setSendWait(false);
    };

    const logout = async () => {
        await Fetch(host + '/logout', 'POST', {});
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        navigate('/login');
    };

    if (wait || !user) {
        return (
            <Box className="flex justify-center py-20">
                <CircularProgress size={70} />
            </Box>
        );
    }

    const memberYear = user.verified_at?.split?.('-')?.[0] || '—';
    const majorLabel = user.major?.name_ar || user.major?.name_en || (isAr ? 'طالب' : 'Student');
    const degreeLabel = user.academic_degree?.name_ar || user.academic_degree?.name_en;
    const studentQr = user?.id ? `student:${user.id}` : '';

    const ProfileActionCard = ({ icon, title, description, onClick, accent = 'blue' }) => {
        const accentColor = accent === 'red' ? '#dc2626' : '#0D358C';
        const isDark = theme.palette.mode === 'dark';

        return (
            <Box
                onClick={onClick}
                className="profile-action-card home-card-hover"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minHeight: 88,
                    px: 2,
                    py: 1.75,
                    borderRadius: '1.25rem',
                    cursor: 'pointer',
                    bgcolor: isDark ? 'rgba(30, 41, 59, 0.6)' : '#f8fafc',
                    border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(13, 53, 140, 0.08)'}`,
                    borderInlineStart: `4px solid ${accentColor}`,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                        bgcolor: isDark ? 'rgba(51, 65, 85, 0.75)' : '#eff6ff',
                        borderColor: isDark ? 'rgba(147, 197, 253, 0.25)' : 'rgba(37, 99, 235, 0.2)',
                    },
                }}
            >
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: isDark ? 'rgba(13, 53, 140, 0.25)' : 'rgba(13, 53, 140, 0.08)',
                        color: accentColor,
                        '& img': { width: 28, height: 28, objectFit: 'contain' },
                        '& svg': { fontSize: 28 },
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.3 }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.5 }}>
                        {description}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box className="w-full" dir={isAr ? 'rtl' : 'ltr'}>
            <PageHero
                size="large"
                title={`${user.first_name} ${user.middle_name ? `${user.middle_name} ` : ''}${user.last_name}`}
                subtitle={`${majorLabel}${degreeLabel ? ` · ${degreeLabel}` : ''} · ${user.email}`}
                icon={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            component="img"
                            src={LogoImage}
                            alt={APP_NAME}
                            sx={{
                                width: { xs: 64, md: 80 },
                                height: { xs: 64, md: 80 },
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))',
                            }}
                        />
                        {user.image ? (
                            <Box
                                sx={{
                                    width: { xs: 88, md: 104 },
                                    height: { xs: 88, md: 104 },
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '4px solid rgba(255,255,255,0.55)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                }}
                            >
                                <img src={user.image} alt="" className="w-full h-full object-cover" />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: { xs: 88, md: 104 },
                                    height: { xs: 88, md: 104 },
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    border: '4px solid rgba(255,255,255,0.55)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                }}
                            >
                                <PermIdentityIcon sx={{ fontSize: { xs: 52, md: 60 }, color: '#fff' }} />
                            </Box>
                        )}
                    </Box>
                }
                actions={
                    <Button
                        variant="contained"
                        onClick={() => setPopup('update', 'flex')}
                        sx={{
                            bgcolor: '#fff',
                            color: '#0D358C',
                            fontWeight: 700,
                            fontSize: { xs: '0.95rem', md: '1.05rem' },
                            px: 2.5,
                            py: 1,
                        }}
                    >
                        <EditOutlinedIcon sx={{ ml: 0.5, fontSize: 20 }} />
                        {isAr ? 'تحديث الملف' : 'Edit profile'}
                    </Button>
                }
            />

            <Grid container spacing={2.5} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12, md: studentQr ? 7 : 12 }}>
                    <DashboardPanelCard
                        title={isAr ? 'معلومات الحساب' : 'Account info'}
                        className="mx-0 h-full"
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {(user.phone_code || user.code) && user.phone && (
                                <Box className="flex items-center gap-1">
                                    <LocalPhoneOutlinedIcon fontSize="small" sx={{ color: '#0D358C' }} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {isAr ? 'الهاتف' : 'Phone'}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} dir="ltr">
                                            +{user.phone_code || user.code}{user.phone}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {isAr ? 'عضو منذ' : 'Member since'}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>{memberYear}</Typography>
                            </Box>
                            {degreeLabel && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {isAr ? 'الدرجة العلمية' : 'Degree'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>{degreeLabel}</Typography>
                                </Box>
                            )}
                        </Box>
                    </DashboardPanelCard>
                </Grid>

                {studentQr && (
                    <Grid size={{ xs: 12, md: 5 }}>
                        <ScrollReveal delay={80}>
                            <DashboardPanelCard title={isAr ? 'رمز QR للتعريف' : 'ID QR code'} className="mx-0 h-full">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                    <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', flexShrink: 0 }}>
                                        <QRCode value={studentQr} size={100} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {user.first_name} {user.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {isAr ? `معرف الحساب: ${user.id}` : `Account ID: ${user.id}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {isAr ? 'اعرض الرمز للأدمن عند شحن المحفظة.' : 'Show this code when topping up your wallet.'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, lg: 7 }}>
                    <ScrollReveal delay={100}>
                        <DashboardPanelCard
                            title={isAr ? 'مسارك التعليمي' : 'Your learning path'}
                            subtitle={isAr ? 'الوصول السريع لدوراتك وشهاداتك' : 'Quick access to courses and certificates'}
                            className="mx-0 h-full"
                        >
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <ProfileActionCard
                                        icon={<MenuBookOutlinedIcon />}
                                        title={isAr ? 'دوراتي' : 'My courses'}
                                        description={isAr ? 'متابعة التقدم والدروس' : 'Track progress and lessons'}
                                        onClick={() => navigate('/my-courses')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <ProfileActionCard
                                        icon={<WorkspacePremiumOutlinedIcon />}
                                        title={isAr ? 'شهاداتي' : 'My certificates'}
                                        description={isAr ? 'تحميل وعرض الشهادات' : 'Download and view certificates'}
                                        onClick={() => navigate('/certificates')}
                                    />
                                </Grid>
                            </Grid>
                        </DashboardPanelCard>
                    </ScrollReveal>
                </Grid>

                <Grid size={{ xs: 12, lg: 5 }}>
                    <ScrollReveal delay={160} direction="left">
                        <DashboardPanelCard
                            title={isAr ? 'الأمان والإعدادات' : 'Security & settings'}
                            subtitle={isAr ? 'إدارة حسابك وتفضيلاتك' : 'Manage account and preferences'}
                            className="mx-0 h-full"
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <ProfileActionCard
                                    icon={<img src={ResetPasswordImage} alt="" />}
                                    title={isAr ? 'إعادة تعيين كلمة المرور' : 'Reset password'}
                                    description={isAr ? 'تحديث كلمة مرور حسابك' : 'Update your account password'}
                                    onClick={() => setPopup('update-password', 'flex')}
                                />
                                <ProfileActionCard
                                    icon={<img src={LanguageImage} alt="" />}
                                    title={isAr ? 'لغة النظام' : 'Language'}
                                    description={isAr ? 'تغيير لغة واجهة المنصة' : 'Change platform language'}
                                    onClick={handleClick}
                                />
                                <ProfileActionCard
                                    icon={<NotificationsNoneOutlinedIcon />}
                                    title={isAr ? 'الإشعارات' : 'Notifications'}
                                    description={isAr ? 'عرض إشعاراتك' : 'View your notifications'}
                                    onClick={() => navigate('/notifications')}
                                />
                                <ProfileActionCard
                                    icon={<VerifiedOutlinedIcon />}
                                    title={isAr ? 'التحقق من شهادة' : 'Verify certificate'}
                                    description={isAr ? 'التحقق من صحة شهادة' : 'Validate a certificate'}
                                    onClick={() => navigate('/certificates?tab=verify')}
                                />
                                <ProfileActionCard
                                    icon={<LogoutIcon />}
                                    title={isAr ? 'تسجيل الخروج' : 'Logout'}
                                    description={isAr ? 'الخروج من حسابك' : 'Sign out of your account'}
                                    onClick={logout}
                                    accent="red"
                                />
                            </Box>
                        </DashboardPanelCard>
                    </ScrollReveal>
                </Grid>
            </Grid>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose} dir="rtl">
                <MenuItem sx={{ background: lang === 'ar' ? '#F3F4F6' : undefined }} onClick={() => { setLang('ar'); changeLanguage('ar'); }}>
                    <ListItemIcon>{sendWait && lang === 'ar' ? <CircularProgress size={20} /> : <img src={SyImage} alt="" />}</ListItemIcon>
                    العربية
                </MenuItem>
                <MenuItem sx={{ background: lang === 'en' ? '#F3F4F6' : undefined }} onClick={() => { setLang('en'); changeLanguage('en'); }}>
                    <ListItemIcon>{sendWait && lang === 'en' ? <CircularProgress size={20} /> : <img src={AuImage} alt="" />}</ListItemIcon>
                    English
                </MenuItem>
            </Menu>

            <Box id="update" className="w-screen h-screen fixed top-0 bg-gray-500/40 hidden justify-center items-center z-[1300]">
                <UpdateProfile onClickCancel={() => setPopup('update', 'none')} setSnackBar={setSnackBar} />
            </Box>
            <Box id="update-password" className="w-screen h-screen fixed top-0 bg-gray-500/40 hidden justify-center items-center z-[1300]">
                <UpdatePassword onClickCancel={() => setPopup('update-password', 'none')} setSnackBar={setSnackBar} />
            </Box>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default Profile;
